import * as borogove from "borogove";
import DOMPurify from "dompurify";
import { useState, useEffect, useRef, useCallback } from "react";
import { useOutletContext, useParams, useNavigate, Link } from "react-router";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { uuidv7 } from "uuidv7";
import { ArrowLeft, Phone, Info, ChevronDown, Paperclip, Send, Bold, Italic, Smile, Check, CheckCheck, Clock, X, Reply, Pencil, Trash2, Upload } from "lucide-react";
import { confirmDialog } from "../ConfirmDialog";
import { cn } from "../../lib/utils";
import { href, useChats, showNotification } from "../../util";
import { adaptMessage } from "../../lib/xmpp";
import { useSettingsStore } from "../../stores/settingsStore";
import { useUIStore } from "../../stores/uiStore";
import { ChatDetails } from "./ChatDetails";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import * as Popover from "@radix-ui/react-popover";

const formatTime = (ts: string | number) => {
	const d = new Date(ts);
	return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDate = (ts: string | number) => {
	const d = new Date(ts);
	return d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
};

const formatDateSeparator = (ts: string | number) => {
	const d = new Date(ts);
	const now = new Date();
	const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
	if (diffDays === 0 && d.getDate() === now.getDate()) return "Today";
	if (diffDays <= 1 && d.getDate() === now.getDate() - 1) return "Yesterday";
	return d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
};

const IMAGE_EXT = /\.(png|jpe?g|gif|webp)$/i;
const VIDEO_EXT = /\.(mp4|webm)$/i;
const AUDIO_EXT = /\.(mp3|ogg|wav)$/i;
const SNIKKET_UPLOAD = /^https?:\/\/share\.[^/]*\.snikket\.chat\/upload\//i;

type MediaType = "image" | "video" | "audio";

const getMediaType = (url: string): MediaType | null => {
	try {
		const { pathname } = new URL(url);
		if (IMAGE_EXT.test(pathname) || SNIKKET_UPLOAD.test(url)) return "image";
		if (VIDEO_EXT.test(pathname)) return "video";
		if (AUDIO_EXT.test(pathname)) return "audio";
	} catch {
		// not a valid URL
	}
	return null;
};

const URL_RE = /https?:\/\/[^\s<>"']+/g;

interface TextSegment { kind: "text"; value: string }
interface MediaSegment { kind: MediaType; url: string }
type Segment = TextSegment | MediaSegment;

const parseSegments = (raw: string): Segment[] => {
	const segments: Segment[] = [];
	let lastIndex = 0;
	for (const match of raw.matchAll(URL_RE)) {
		const url = match[0];
		const mediaType = getMediaType(url);
		if (mediaType) {
			const before = raw.slice(lastIndex, match.index);
			if (before.trim()) segments.push({ kind: "text", value: before });
			segments.push({ kind: mediaType, url });
			lastIndex = match.index! + url.length;
		}
	}
	const tail = raw.slice(lastIndex);
	if (tail.trim()) segments.push({ kind: "text", value: tail });
	return segments;
};


const linkifyText = (html: string): string => {
	const urlRegex = /(https?:\/\/[^\s<]+)/g;
	return html.replace(urlRegex, (url) => {
		return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="underline text-purple-300 hover:text-purple-200 break-all">${url}</a>`;
	});
};

const getDomain = (url: string): string | null => {
	try { return new URL(url).hostname; } catch { return null; }
};

const extractNonMediaUrls = (text: string): string[] => {
	const matches = text.match(URL_RE) || [];
	return matches.filter((url) => !getMediaType(url));
};

const LinkPreviewCard = ({ url }: { url: string }) => {
	const domain = getDomain(url);
	if (!domain) return null;

	const isYouTube = /^(www\.)?(youtube\.com|youtu\.be)$/i.test(domain);
	let label = domain;
	if (isYouTube) {
		try {
			const urlObj = new URL(url);
			const videoId = urlObj.searchParams.get("v") || urlObj.pathname.split("/").pop();
			label = `\u25B6 YouTube: ${videoId}`;
		} catch {
			label = `\u25B6 YouTube`;
		}
	}

	return (
		<a href={url} target="_blank" rel="noopener noreferrer" className="block bg-white/5 rounded-lg px-3 py-2 mt-2 text-xs text-white/50 hover:bg-white/10 transition-colors truncate">
			{label}
		</a>
	);
};

const MediaBlock = ({ segment }: { segment: MediaSegment }) => {
	if (segment.kind === "image") {
		return (
			<a href={segment.url} target="_blank" rel="noopener noreferrer">
				<img
					src={segment.url}
					alt=""
					loading="lazy"
					className="rounded-xl overflow-hidden max-w-full max-h-80 object-contain"
				/>
			</a>
		);
	}
	if (segment.kind === "video") {
		return (
			<video
				src={segment.url}
				controls
				preload="metadata"
				className="rounded-xl overflow-hidden max-w-full max-h-80"
			/>
		);
	}
	return (
		<audio src={segment.url} controls preload="metadata" className="max-w-full" />
	);
};

const DeliveryIndicator = ({ message }: { message: borogove.ChatMessage }) => {
	const isRead = (message as any).isRead?.();
	const isDelivered = (message as any).isDelivered?.();
	const status = (message as any).status ?? (message as any).deliveryStatus;

	if (isRead || status === "read") {
		return <CheckCheck size={14} className="inline-block ml-1 align-bottom text-blue-400" />;
	}
	if (isDelivered || message.serverId || status === "delivered" || status === "sent") {
		return <Check size={14} className="inline-block ml-1 align-bottom text-white/60" />;
	}
	return <Clock size={12} className="inline-block ml-1 align-bottom text-white/40" />;
};

const senderColors = [
	"text-blue-400", "text-green-400", "text-yellow-400", "text-pink-400",
	"text-cyan-400", "text-orange-400", "text-violet-400", "text-emerald-400",
];

const getSenderColor = (senderId: string) => {
	let hash = 0;
	for (let i = 0; i < senderId.length; i++) {
		hash = ((hash << 5) - hash) + senderId.charCodeAt(i);
	}
	return senderColors[Math.abs(hash) % senderColors.length];
};

const MessageBubble = ({
	message,
	showSenderName,
	onReply,
	onEdit,
	onRetract,
}: {
	message: borogove.ChatMessage;
	showSenderName: boolean;
	onReply: (message: borogove.ChatMessage) => void;
	onEdit?: (message: borogove.ChatMessage) => void;
	onRetract?: (message: borogove.ChatMessage) => void;
}) => {
	const isOutgoing = !(message as any).isIncoming?.();
	const isRetracted = (message as any).isRetracted?.();

	if (isRetracted) {
		return <div className="text-xs text-white/30 italic px-4 py-1">This message was deleted</div>;
	}

	const raw = message.text ?? "";
	const segments = parseSegments(raw);
	const hasMedia = segments.some((s) => s.kind !== "text");
	const mediaOnly = hasMedia && segments.every((s) => s.kind !== "text");
	const isEdited = (message as any).isEdited?.();
	const replyToId = (message as any).replyTo ?? (message as any).getReplyTo?.();

	const replyQuote = replyToId && (
		<div className="mb-2 pl-3 border-l-2 border-primary/50 text-xs text-white/40">
			Replied message
		</div>
	);

	const editedLabel = isEdited && (
		<span className="text-xs text-white/30 ml-1">(edited)</span>
	);

	const timestamp = (
		<span className={cn("text-xs ml-2 align-bottom", isOutgoing ? "text-white/60" : "text-white/40")}>
			{formatTime(message.timestamp)}
			{editedLabel}
			{isOutgoing && <DeliveryIndicator message={message} />}
		</span>
	);

	const senderDisplayName = (message as any).senderName || (message as any).senderId?.split("/")?.[1] || (message as any).senderId || "";
	const senderId = (message as any).senderId || "";
	const senderName = showSenderName && !isOutgoing && (
		<p className={cn("text-xs font-medium mb-1", getSenderColor(senderId))}>
			{senderDisplayName}
		</p>
	);

	const replyButton = (
		<button
			onClick={() => onReply(message)}
			className="opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 shrink-0"
			title="Reply"
		>
			<Reply size={14} />
		</button>
	);

	const actionButtons = isOutgoing && (
		<div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
			<button onClick={() => onEdit?.(message)} className="w-6 h-6 rounded flex items-center justify-center text-white/30 hover:text-white/60" title="Edit message">
				<Pencil size={12} />
			</button>
			<button onClick={() => onRetract?.(message)} className="w-6 h-6 rounded flex items-center justify-center text-white/30 hover:text-red-400/60" title="Delete message">
				<Trash2 size={12} />
			</button>
		</div>
	);

	if (mediaOnly) {
		return (
			<div className={cn("flex items-center gap-1 px-4 py-0.5 group", isOutgoing ? "flex-row-reverse" : "flex-row")}>
				<div className="max-w-[75%] md:max-w-[65%] flex flex-col gap-1">
					{replyQuote}
					{senderName}
					{segments.map((seg, i) =>
						seg.kind !== ("text" as string) ? <MediaBlock key={i} segment={seg} /> : null,
					)}
					<span className={cn("text-xs", isOutgoing ? "text-white/60 text-right" : "text-white/40")}>
						{formatTime(message.timestamp)}
						{editedLabel}
						{isOutgoing && <DeliveryIndicator message={message} />}
					</span>
				</div>
				{replyButton}
				{actionButtons}
			</div>
		);
	}

	const sanitized = raw ? linkifyText(DOMPurify.sanitize(raw, { ADD_ATTR: ['target', 'rel'] })) : "";
	const nonMediaUrls = extractNonMediaUrls(raw);

	return (
		<div className={cn("flex items-center gap-1 px-4 py-0.5 group", isOutgoing ? "flex-row-reverse" : "flex-row")}>
			<div
				className={cn(
					"max-w-[75%] md:max-w-[65%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed break-words",
					isOutgoing
						? "bg-primary text-white rounded-br-sm"
						: "bg-white/10 text-white/90 rounded-bl-sm",
				)}
			>
				{replyQuote}
				{senderName}
				{hasMedia ? (
					<div className="flex flex-col gap-2">
						{segments.map((seg, i) =>
							seg.kind === "text" ? (
								<span key={i} dangerouslySetInnerHTML={{ __html: linkifyText(DOMPurify.sanitize(seg.value, { ADD_ATTR: ['target', 'rel'] })) }} />
							) : (
								<MediaBlock key={i} segment={seg} />
							),
						)}
					</div>
				) : (
					<>
						<span dangerouslySetInnerHTML={{ __html: sanitized }} />
						{nonMediaUrls.map((url, i) => (
							<LinkPreviewCard key={i} url={url} />
						))}
					</>
				)}
				{timestamp}
			</div>
			{actionButtons}
		</div>
	);
};

const TypingIndicator = () => (
	<div className="flex gap-2 px-4 py-1">
		<div className="bg-white/10 rounded-2xl rounded-bl-sm px-4 py-2.5 flex items-center gap-1">
			<span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "0ms" }} />
			<span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "150ms" }} />
			<span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "300ms" }} />
		</div>
	</div>
);

const MessageInput = ({
	onSend,
	onTyping,
	disabled,
	replyTo,
	onCancelReply,
}: {
	onSend: (text: string, files: File[]) => void;
	onTyping?: (isTyping: boolean) => void;
	disabled?: boolean;
	replyTo: borogove.ChatMessage | null;
	onCancelReply: () => void;
}) => {
	const { enterIsSend } = useSettingsStore();
	const [emojiOpen, setEmojiOpen] = useState(false);
	const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const editor = useEditor({
		extensions: [
			StarterKit,
			Placeholder.configure({ placeholder: "Type a message\u2026" }),
		],
		editorProps: {
			attributes: {
				class: "outline-none text-base text-white/90 min-h-[1.5rem] max-h-32 overflow-y-auto",
			},
		},
		onUpdate: () => {
			// Signal composing on every content change
			onTyping?.(true);
			// Reset the pause timer -- send "paused" after 3s of inactivity
			if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
			pauseTimerRef.current = setTimeout(() => {
				onTyping?.(false);
			}, 3000);
		},
	});

	// Cleanup pause timer on unmount
	useEffect(() => {
		return () => {
			if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
		};
	}, []);

	const onEmojiSelect = (emoji: any) => {
		editor?.chain().focus().insertContent(emoji.native).run();
		setEmojiOpen(false);
	};

	const handleSend = () => {
		const text = editor?.getText() ?? "";
		if (text.trim() || attachedFiles.length > 0) {
			// Clear typing state on send
			if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
			onTyping?.(false);
			onSend(text.trim(), attachedFiles);
			editor?.commands.clearContent();
			setAttachedFiles([]);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (enterIsSend && e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (files) {
			setAttachedFiles((prev) => [...prev, ...Array.from(files)]);
		}
		e.target.value = "";
	};

	const removeFile = (index: number) => {
		setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
	};

	return (
		<div className="glass-panel border-t border-white/8 p-3 pb-safe">
			{/* Reply preview bar */}
			{replyTo && (
				<div className="px-1 py-2 mb-2 flex items-center gap-3">
					<div className="w-1 h-8 bg-primary rounded-full shrink-0" />
					<div className="flex-1 min-w-0">
						<p className="text-xs text-primary-light font-medium">Reply</p>
						<p className="text-xs text-white/50 truncate">{replyTo.text}</p>
					</div>
					<button onClick={onCancelReply} className="text-white/40 hover:text-white transition-colors shrink-0">
						<X size={16} />
					</button>
				</div>
			)}

			{/* Attachment preview chips */}
			{attachedFiles.length > 0 && (
				<div className="flex flex-wrap gap-1.5 mb-2">
					{attachedFiles.map((file, i) => (
						<div
							key={`${file.name}-${i}`}
							className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 text-white/80 text-xs max-w-[200px]"
						>
							<span className="truncate">{file.name}</span>
							<button
								onClick={() => removeFile(i)}
								className="shrink-0 w-4 h-4 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
							>
								<X size={10} />
							</button>
						</div>
					))}
				</div>
			)}

			<div className="flex items-end gap-2">
				{/* Toolbar */}
				<div className="flex items-center gap-1">
					<button
						onClick={() => editor?.chain().focus().toggleBold().run()}
						className={cn(
							"w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all text-xs font-bold",
							editor?.isActive("bold") && "bg-white/15 text-white",
						)}
						title="Bold"
					>
						B
					</button>
					<button
						onClick={() => editor?.chain().focus().toggleItalic().run()}
						className={cn(
							"w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all text-xs italic",
							editor?.isActive("italic") && "bg-white/15 text-white",
						)}
						title="Italic"
					>
						I
					</button>
					<button
						onClick={() => fileInputRef.current?.click()}
						className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all"
						title="Attach file"
					>
						<Paperclip size={16} />
					</button>
					<input
						ref={fileInputRef}
						type="file"
						accept="image/*,video/*,audio/*,application/*"
						multiple
						onChange={handleFileSelect}
						className="hidden"
					/>
					<Popover.Root open={emojiOpen} onOpenChange={setEmojiOpen}>
						<Popover.Trigger asChild>
							<button
								className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all"
								title="Emoji"
							>
								<Smile size={16} />
							</button>
						</Popover.Trigger>
						<Popover.Portal>
							<Popover.Content
								side="top"
								sideOffset={8}
								className="bg-[#1a1a24] border border-white/12 rounded-xl shadow-2xl z-50"
							>
								<Picker
									data={data}
									onEmojiSelect={onEmojiSelect}
									theme="dark"
								/>
							</Popover.Content>
						</Popover.Portal>
					</Popover.Root>
				</div>

				{/* Text input */}
				<div
					className="flex-1 min-w-0 px-4 py-2.5 rounded-2xl bg-white/8 border border-white/8 focus-within:border-white/20 transition-colors"
					onKeyDown={handleKeyDown}
				>
					<EditorContent editor={editor} />
				</div>

				{/* Send button */}
				<button
					onClick={handleSend}
					disabled={disabled}
					className="w-10 h-10 rounded-full bg-primary hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors shrink-0"
				>
					<Send size={18} className="text-white translate-x-0.5" />
				</button>
			</div>
		</div>
	);
};

export const Chat = () => {
	const { accounts, client, accountId: ctxAccountId } = useOutletContext<any>();
	const params = useParams();
	const navigate = useNavigate();
	const virtuosoRef = useRef<VirtuosoHandle>(null);
	const [messages, setMessages] = useState<borogove.ChatMessage[]>([]);
	const [atBottom, setAtBottom] = useState(true);
	const [messagesLoaded, setMessagesLoaded] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [remoteTyping, setRemoteTyping] = useState(false);
	const [editingMessage, setEditingMessage] = useState<borogove.ChatMessage | null>(null);
	const [replyTo, setReplyTo] = useState<borogove.ChatMessage | null>(null);
	const [lastReadId, setLastReadId] = useState<string | null>(null);

	const accountId = ctxAccountId || params.accountId!;
	const chatId = params.chatId!;
	const account = client ? { client } : accounts?.[accountId];
	const chat: borogove.Chat | undefined = account?.client?.getChat(chatId);

	const chatName = chat?.getDisplayName() || chatId;
	const participants = chat?.getParticipants?.() || [];
	const isGroup = (chat as any) instanceof borogove.Channel || participants.length > 2;
	const canCall = (chat as any)?.canAudioCall?.() ?? false;
	const onMobile = window.innerWidth < 768;
	const { setActiveCall, detailPanelOpen, setDetailPanelOpen } = useUIStore();
	const [isDragging, setIsDragging] = useState(false);

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		// Only set false if leaving the container, not entering a child
		if (e.currentTarget === e.target) {
			setIsDragging(false);
		}
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);

		const files = Array.from(e.dataTransfer.files);
		if (files.length > 0) {
			files.forEach(async (file) => {
				try {
					await (chat as any).sendFile?.(file);
				} catch (err) {
					console.error("File upload failed:", err);
				}
			});
		}
	};

	const handlePaste = (e: React.ClipboardEvent) => {
		const items = Array.from(e.clipboardData.items);
		const files = items
			.filter(item => item.kind === "file")
			.map(item => item.getAsFile())
			.filter(Boolean) as File[];
		if (files.length > 0) {
			files.forEach(async (file) => {
				await (chat as any).sendFile?.(file);
			});
		}
	};

	// Load initial messages async (MAM fetch from server) + mark chat as active (clears unread)
	useEffect(() => {
		if (!chat) return;
		let cancelled = false;
		setMessagesLoaded(false);
		setMessages([]);
		(chat as any).setActive?.(true, null);
		(chat as any).getMessagesBefore(null, null).then((msgs: borogove.ChatMessage[]) => {
			if (!cancelled) {
				setMessages(msgs);
				setMessagesLoaded(true);
				// Mark all loaded messages as read
				if (msgs.length > 0) {
					chat.markReadUpTo(msgs[msgs.length - 1]);
				}
			}
		}).catch((err: any) => {
			console.error("Failed to load messages:", err);
			if (!cancelled) setMessagesLoaded(true);
		});
		return () => {
			cancelled = true;
			(chat as any).setActive?.(false, null);
		};
	}, [chatId]);

	// Listen for new incoming/outgoing messages in real time
	useEffect(() => {
		if (!account?.client || !chat) return;
		const token = account.client.addChatMessageListener?.(
			(message: borogove.ChatMessage, eventType: any) => {
				// chatId() is a method on ChatMessage
				const msgChatId = (message as any).chatId?.();
				if (msgChatId !== chatId) return;
				setMessages((prev) => {
					const updateIdx = prev.findIndex(
						(m) =>
							(m.serverId && message.serverId && m.serverId === message.serverId) ||
							(m.localId && message.localId && m.localId === message.localId),
					);
					if (updateIdx > -1) {
						// Update existing message (delivery receipt, edit, etc.)
						return [...prev.slice(0, updateIdx), message, ...prev.slice(updateIdx + 1)];
					}
					// Append new message
					return [...prev, message];
				});
				if ((message as any).isIncoming?.()) {
					try {
						showNotification(accountId, adaptMessage(message));
					} catch {
						// adapter rejected a malformed message; swallow rather
						// than crash the chat — REQ-CN-001 acceptance.
					}
					chat.markReadUpTo(message);
				}
			},
		);

		// Listen for read receipts (XEP-0333)
		const readToken = (account.client as any).addReadReceiptListener?.((cid: string, mid: string) => {
			if (cid === chatId) setLastReadId(mid);
		});

		// Also check if the chat object exposes the last read marker directly
		const peerReadId = (chat as any).lastReadByPeer?.();
		if (peerReadId) setLastReadId(peerReadId);

		return () => {
			if (token) account.client.removeEventListener(token);
			if (readToken) account.client.removeEventListener(readToken);
		};
	}, [account?.client, chatId, accountId]);

	// Listen for remote chat state (typing indicators, XEP-0085)
	useEffect(() => {
		if (!account?.client || !chat) return;
		setRemoteTyping(false);

		// Try client-level chat state listener first
		const chatStateToken = account.client.addChatStateListener?.((cid: string, state: string) => {
			if (cid === chatId) {
				setRemoteTyping(state === "composing");
			}
		});

		// Also try chat-level typing listener
		const typingToken = (chat as any).addTypingListener?.((isTyping: boolean) => {
			setRemoteTyping(isTyping);
		});

		return () => {
			if (chatStateToken) account.client.removeEventListener(chatStateToken);
			if (typingToken) (chat as any).removeEventListener?.(typingToken);
		};
	}, [account?.client, chatId]);

	// Send local chat state to remote peer
	const handleTyping = useCallback((isTyping: boolean) => {
		if (!chat) return;
		(chat as any).sendChatState?.(isTyping ? "composing" : "active");
	}, [chat]);

	// Load more messages when scrolled to top
	const handleStartReached = useCallback(async () => {
		if (!chat || loadingMore || messages.length === 0) return;
		const first = messages[0];
		setLoadingMore(true);
		try {
			const older = await (chat as any).getMessagesBefore(
				first.serverId || first.localId,
				first.timestamp,
			);
			if (older.length > 0) {
				setMessages((prev) => [...older, ...prev]);
			}
		} catch (err) {
			console.error("Failed to load older messages:", err);
		} finally {
			setLoadingMore(false);
		}
	}, [chat, messages, loadingMore]);

	const handleSend = useCallback(async (text: string, files: File[] = []) => {
		if (!chat) return;
		try {
			// If editing an existing message, send a correction (XEP-0308)
			if (editingMessage) {
				const corrected = new borogove.ChatMessageBuilder();
				corrected.text = text;
				(corrected as any).replaceId = editingMessage.serverId || editingMessage.localId;
				(corrected as any).setCorrection?.(editingMessage.serverId);
				await (chat as any).sendMessage(corrected);
				setEditingMessage(null);
				return;
			}

			// Handle file uploads via HTTP Upload (XEP-0363)
			for (const file of files) {
				try {
					const attachment = await (chat as any).prepareAttachment?.(file);
					if (attachment) {
						const fileMsg = new borogove.ChatMessageBuilder();
						fileMsg.localId = uuidv7();
						(fileMsg as any).attachments = [attachment];
						if (files.length === 1 && text) {
							fileMsg.text = text;
						}
						// Set reply-to reference (XEP-0461) on file message if replying
						if (replyTo && files.length === 1) {
							const replyId = replyTo.serverId || replyTo.localId;
							(fileMsg as any).replyTo = replyId;
							(fileMsg as any).setReplyTo?.(replyId);
						}
						await (chat as any).sendMessage(fileMsg);
					} else if ((chat as any).sendFile) {
						await (chat as any).sendFile(file);
					} else {
						console.warn("File upload not supported by SDK:", file.name);
					}
				} catch (fileErr) {
					console.error("File upload failed:", file.name, fileErr);
				}
			}
			// Send text if present and not already sent with a single-file message
			if (text && files.length !== 1) {
				const message = new borogove.ChatMessageBuilder();
				message.localId = uuidv7();
				message.text = text;
				// Set reply-to reference (XEP-0461)
				if (replyTo) {
					const replyId = replyTo.serverId || replyTo.localId;
					(message as any).replyTo = replyId;
					(message as any).setReplyTo?.(replyId);
				}
				await (chat as any).sendMessage(message);
			}
		} catch (err) {
			console.error("Send failed:", err);
		}
		setReplyTo(null);
	}, [chat, editingMessage, replyTo]);

	const handleEdit = useCallback((message: borogove.ChatMessage) => {
		setEditingMessage(message);
	}, []);

	const handleRetract = useCallback(async (message: borogove.ChatMessage) => {
		const confirmed = await confirmDialog("Are you sure you want to delete this message?");
		if (!confirmed) return;
		try {
			await (chat as any).retractMessage?.(message.serverId || message.localId);
			if (!(chat as any).retractMessage) {
				await (chat as any).retract?.(message);
			}
		} catch (err) {
			console.error("Retract failed:", err);
		}
	}, [chat]);

	if (!chat) {
		return (
			<div className="flex flex-col h-full items-center justify-center text-white/40 gap-3">
				<p className="text-sm">Chat not found</p>
			</div>
		);
	}

	return (
		<div className="flex h-full overflow-hidden">
		<div
			className="flex flex-col h-full flex-1 min-w-0 relative"
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			onPaste={handlePaste}
		>
			{/* Drop zone overlay */}
			{isDragging && (
				<div className="absolute inset-0 z-40 flex items-center justify-center bg-primary/20 backdrop-blur-sm border-2 border-dashed border-primary rounded-2xl m-2 pointer-events-none">
					<div className="flex flex-col items-center gap-3 text-white">
						<Upload size={48} className="text-primary-light" />
						<p className="text-lg font-medium">Drop files to send</p>
						<p className="text-sm text-white/50">Images, videos, documents</p>
					</div>
				</div>
			)}

			{/* Header */}
			<header className="flex items-center gap-3 px-4 py-3 glass-panel border-b border-white/8 shrink-0">
				{onMobile && (
					<button
						onClick={() => navigate(href("/accounts/:accountId/chats", { accountId }))}
						className="w-9 h-9 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
					>
						<ArrowLeft size={20} />
					</button>
				)}

				{/* Avatar */}
				<div className="w-9 h-9 rounded-full bg-primary/50 flex items-center justify-center text-white text-sm font-medium shrink-0">
					{chatName.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase()}
				</div>

				<div className="flex-1 min-w-0">
					<p className="text-sm font-medium text-white truncate">{chatName}</p>
					<p className="text-xs text-white/50 truncate">
						{isGroup ? `${participants.length} members` : chatId}
					</p>
					{(() => {
						const presenceColors: Record<string, string> = {
							online: "bg-green-400",
							away: "bg-yellow-400",
							dnd: "bg-red-400",
							offline: "bg-gray-500",
						};
						const presenceLabels: Record<string, string> = {
							online: "Online",
							away: "Away",
							dnd: "Do Not Disturb",
							offline: "Offline",
						};
						const presence = (chat as any).getParticipantDetails?.(chatId)?.presence;
						const presenceStr = presence ? String(presence).toLowerCase() : undefined;
						const lastSeen = (chat as any).getParticipantDetails?.(chatId)?.lastSeen;
						const lastSeenText = lastSeen ? (() => {
							const diff = Date.now() - new Date(lastSeen).getTime();
							const mins = Math.floor(diff / 60000);
							if (mins < 1) return "Last seen just now";
							if (mins < 60) return `Last seen ${mins} minute${mins > 1 ? "s" : ""} ago`;
							const hours = Math.floor(mins / 60);
							if (hours < 24) return `Last seen ${hours} hour${hours > 1 ? "s" : ""} ago`;
							const days = Math.floor(hours / 24);
							return `Last seen ${days} day${days > 1 ? "s" : ""} ago`;
						})() : undefined;
						if (!presenceStr || !presenceColors[presenceStr]) return null;
						return (
							<span className="flex items-center gap-1.5">
								<span className={cn("w-1.5 h-1.5 rounded-full", presenceColors[presenceStr])} />
								<span className="text-xs text-white/40">
									{presenceLabels[presenceStr] || presenceStr}
									{presenceStr === "offline" && lastSeenText && ` \u00B7 ${lastSeenText}`}
								</span>
							</span>
						);
					})()}
				</div>

				<div className="flex items-center gap-1">
					{canCall && (
						<button
							onClick={async () => {
								try {
									await navigator.mediaDevices.getUserMedia({ audio: true });
								} catch (err) {
									console.error("Microphone permission denied:", err);
									return;
								}
								try {
									const session = (chat as any)?.startCall?.(true, false);
									if (session) {
										setActiveCall({ session, isIncoming: false, chatId, accountId, hasVideo: false, screenSharing: false });
										session.ring?.();
									} else {
										console.error("startCall returned no session");
									}
								} catch (err) {
									console.error("startCall failed:", err);
								}
							}}
							className="w-9 h-9 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
							title="Start audio call"
						>
							<Phone size={18} />
						</button>
					)}
					<button
						onClick={() => setDetailPanelOpen(!detailPanelOpen)}
						className={cn(
							"w-9 h-9 rounded-lg flex items-center justify-center transition-all",
							detailPanelOpen
								? "text-white bg-white/15"
								: "text-white/60 hover:text-white hover:bg-white/10",
						)}
						title="Details"
					>
						<Info size={18} />
					</button>
				</div>
			</header>

			{/* Messages */}
			<div className="flex-1 overflow-hidden relative">
				{!messagesLoaded ? (
					<div className="flex items-center justify-center h-full">
						<div className="w-6 h-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
					</div>
				) : messages.length === 0 ? (
					<div className="flex flex-col items-center justify-center h-full gap-2 text-white/40">
						<p className="text-sm">No messages yet</p>
						<p className="text-xs">Say hello!</p>
					</div>
				) : (
					<Virtuoso
						ref={virtuosoRef}
						style={{ height: "100%" }}
						data={messages}
						followOutput="auto"
						atBottomStateChange={setAtBottom}
						initialTopMostItemIndex={messages.length - 1}
						startReached={handleStartReached}
						itemContent={(index, message) => {
							const prevMessage = index > 0 ? messages[index - 1] : null;
							const showDate = !prevMessage ||
								new Date(message.timestamp).toDateString() !== new Date(prevMessage.timestamp).toDateString();
							const isOutgoing = !(message as any).isIncoming?.();
							const isLastRead = isOutgoing && lastReadId != null &&
								(message.serverId === lastReadId || message.localId === lastReadId);
							const readBy = isGroup ? ((message as any).readBy?.() ?? []) : [];
							return (
								<>
									{showDate && (
										<div className="flex items-center justify-center py-3 px-4">
											<div className="px-3 py-1 rounded-full bg-white/8 text-xs text-white/40">
												{formatDateSeparator(message.timestamp)}
											</div>
										</div>
									)}
									<MessageBubble
										key={message.localId || message.serverId}
										message={message}
										showSenderName={isGroup}
										onReply={setReplyTo}
										onEdit={handleEdit}
										onRetract={handleRetract}
									/>
									{isLastRead && !isGroup && (
										<div className="text-right px-4 -mt-0.5 mb-1">
											<span className="text-[10px] text-white/30">Read</span>
										</div>
									)}
									{isOutgoing && isGroup && readBy.length > 0 && (
										<div className="text-right px-4 -mt-0.5 mb-1">
											<span className="text-[10px] text-white/30">Read by {readBy.length}</span>
										</div>
									)}
								</>
							);
						}}
					/>
				)}

				{/* Scroll to bottom button */}
				{!atBottom && (
					<button
						onClick={() => virtuosoRef.current?.scrollToIndex({ index: messages.length - 1, behavior: "smooth" })}
						className="absolute bottom-4 right-4 w-9 h-9 rounded-full glass-raised flex items-center justify-center text-white/70 hover:text-white transition-all shadow-lg"
					>
						<ChevronDown size={18} />
					</button>
				)}
			</div>

			{/* Editing indicator */}
			{editingMessage && (
				<div className="flex items-center gap-2 px-4 py-2 bg-white/5 border-t border-white/8">
					<Pencil size={14} className="text-primary-light shrink-0" />
					<div className="flex-1 min-w-0">
						<p className="text-xs text-primary-light font-medium">Editing message</p>
						<p className="text-xs text-white/50 truncate">{editingMessage.text}</p>
					</div>
					<button
						onClick={() => setEditingMessage(null)}
						className="w-6 h-6 rounded flex items-center justify-center text-white/40 hover:text-white/70 transition-colors shrink-0"
					>
						<X size={14} />
					</button>
				</div>
			)}

			{/* Typing indicator */}
			{remoteTyping && <TypingIndicator />}

			{/* Input */}
			<MessageInput onSend={handleSend} onTyping={handleTyping} replyTo={replyTo} onCancelReply={() => setReplyTo(null)} />
		</div>

		{/* Details side panel */}
		<div
			className={cn(
				"h-full transition-all duration-200 ease-in-out overflow-hidden shrink-0",
				detailPanelOpen ? "w-72" : "w-0",
			)}
		>
			{detailPanelOpen && <ChatDetails chat={chat} chatId={chatId} accountId={accountId} isGroup={isGroup} participants={participants} />}
		</div>
		</div>
	);
};
